import {lshGETAUTH, EApplicationStateType} from './core';
import {graphqlRequestHandler} from './core';
import {
  IAiBusinessPlan,
  IApplicationOwnershipType,
  ICreateApplicationOwnershipDto,
  ICustomerSupplier,
  IFarmRiskBasic,
  IFarmRiskByApp,
  ICreateFarmRiskDto,
  ICompanyItcDoc,
  IFdAgreementDocType,
  IFdApplicationAgreementType,
  IFdApplicationDocType,
  IFdApplicationType,
  IFdCrop,
  IFdCompany,
  IFdFarm,
  IFdFarmAssetList,
  IFdFinancialPerformance,
  IFdFundingAsk,
  IFdFundingRequestCost,
  IFdLiability,
  IFdOperatingCost,
  IFdProductionCost,
  IFdProductionHistory,
  IFdProductionPlan,
  IFdUser,
  IFarmTeam,
  IGenericType,
  EGPAY_ACCOUNT_TYPE,
  IGpayAccount,
  IitcDoc,
  ILinkedGpayAccount,
  IPerformCompanyCreditCheckDto,
  IRunItcTraderApplicationDto,
  ISaveFdDoc,
  ISaveFdFarmAsset,
  ISaveFdFinancialPerformance,
  ISaveFdOperatingCost,
  ISaveFdProductionHistory,
  ISaveFdProductionPlanDto,
  ISaveProductionCost,
  ISaveLiability,
  ITraderApplication,
  ITradingForm,
  IUpdateFdFarm,
} from 'constants/interfaces';

export interface IFdProjectedRevenueWindow {
  startMonth: string;
  endMonth: string;
}

export interface IFdProjectedRevenueLookup {
  id: string;
  cropId: string;
  cropName: string;
  year: number;
  monthlyCosts: string | null;
  window: IFdProjectedRevenueWindow;
}

interface IProjectedRevenueRecord {
  id: string | number;
  monthlyCosts: string | null;
  year: number;
  crop?: {
    id?: string | number;
    name?: string;
  } | null;
}

function lshParseProjectedRevenueWindow(
  raw: string | null | undefined,
): IFdProjectedRevenueWindow | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      startMonth: String((parsed as any).startMonth ?? ''),
      endMonth: String((parsed as any).endMonth ?? ''),
    };
  } catch (error) {
    console.error('Failed to parse projected revenue monthlyCosts', error);
    return null;
  }
}

export interface ISubmitTraderFormFieldInput {
  id: string;
  value: string;
  fieldTypeName: string;
  isOptional?: boolean;
}

export interface ISubmitTraderFormFieldsResponse {
  success: boolean;
  message: string;
}

type CreateFarmTeamDTO = {
  id?: number;
  name: string;
  description?: string | null;
  farmTeamRoleId: number;
  applicationId: number;
};

type CreateCustomerSupplierDto = {
  id?: number;
  applicationId: number;
  customerId: number;
  cropId: number;
};

type CreateFarmerSupplierDto = {
  id?: number;
  supplierId: number;
  supplierProductId: number;
  applicationId: number;
};

type IFdCommodityType = {
  id: number;
  name: string;
};

type IFdApplicationHistoryCommodity = {
  id: number;
  commodityType: IFdCommodityType;
};

type ICreateOrUpdateFdApplicationHistoryCommodityDto = {
  applicationId: number;
  commodityTypeId: number;
};

type IFdLivestockCatalogueItem = {
  id: number;
  name: string;
  livestockType?: string | null;
  livestockProduct?: string | null;
};

type IFdApplicationLivestockRecord = {
  id?: number;
  quantity?: number | null;
  notes?: string | null;
  type?: string | null;
  productGrading?: string | null;
  sellOfPeriod?: string | null;
  price?: number | null;
  livestock?: IFdLivestockCatalogueItem | null;
  application?: {
    id: number;
  } | null;
};

type IUpsertFdApplicationLivestockDto = {
  id?: number;
  applicationId: number;
  livestockId: number;
  quantity: number;
  notes?: string;
  type?: string;
  productGrading?: string;
  sellOfPeriod?: string;
  price?: number;
};

type IFdApplicationLongTermCropRecord = {
  id?: number;
  blockNumber?: string | null;
  age?: number | null;
  numberOfTrees?: number | null;
  area?: number | null;
  trellies?: boolean | null;
  spacing?: string | null;
  irrigation?: string | null;
  type?: string | null;
  notes?: string | null;
  projectedYear?: number | null;
  projectedYield?: number | null;
  projectedPrice?: number | null;
  projectedArea?: number | null;
  historyYear1?: number | null;
  historyYield1?: number | null;
  historyArea1?: number | null;
  historyYear2?: number | null;
  historyYield2?: number | null;
  historyArea2?: number | null;
  historyYear3?: number | null;
  historyYield3?: number | null;
  historyArea3?: number | null;
  application?: {
    id: number;
  } | null;
  crop?: {
    id: number;
    name: string;
  } | null;
};

type IUpsertFdApplicationLongTermCropDto = {
  id?: number;
  applicationId: number;
  cropId: number;
  blockNumber: string;
  age: number;
  numberOfTrees: number;
  area: number;
  trellies: boolean;
  spacing: number;
  irrigation: string;
  type: string;
  notes?: string;
  projectedYear: number;
  projectedYield: number;
  projectedPrice: number;
  projectedArea: number;
  historyYear1: number;
  historyYield1: number;
  historyArea1: number;
  historyYear2: number;
  historyYield2: number;
  historyArea2: number;
  historyYear3: number;
  historyYield3: number;
  historyArea3: number;
};

type IFdApplicationWaterRightRecord = {
  id?: number;
  scheme?: string | null;
  schedule?: string | null;
  waterSource?: string[] | null;
  waterRights?: boolean | null;
  waterScale?: string | null;
  permitNumber?: string | null;
  controlled?: boolean | null;
  notes?: string | null;
  householdConsumption?: string[] | null;
  waterQuantity?: number | null;
};

type IUpsertFdApplicationWaterRightDto = {
  id?: number;
  applicationId: number;
  waterRights: boolean;
  waterSource: string[];
  householdConsumption: string[];
  scheme: string;
  permitNumber: string;
  schedule?: string;
  waterScale?: string | null;
  waterQuantity?: number | null;
  controlled: boolean;
  notes?: string;
};

type IFdApplicationPropertyRecord = {
  id?: number;
  nameOfDeed?: string | null;
  areaDistrict?: string | null;
  farmArrangement?: string | null;
  term?: number | null;
  amount?: number | null;
  dryland?: number | null;
  irrigation?: number | null;
  pastures?: number | null;
  grazing?: number | null;
  intensive?: number | null;
  other?: number | null;
  notes?: string | null;
  total?: number | null;
};

type IUpsertFdApplicationPropertyDto = {
  id?: number;
  applicationId: number;
  nameOfDeed: string;
  areaDistrict: string;
  farmArrangement?: string | null;
  term?: number | null;
  amount?: number | null;
  dryland?: number | null;
  irrigation?: number | null;
  pastures?: number | null;
  grazing?: number | null;
  intensive?: number | null;
  other?: number | null;
  notes?: string;
};

export async function apiGetAllFdApplications(): Promise<IFdApplicationType[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query{
            getAllFDApplications{
              id
              createdAt
              applicationState
              completedSections
              incompleteSections
              completedSectionsPercentage
              applyingUser{
                id
                name
                surname
                email
                phoneNumber
                creditScore
              }
              company{
                id
                name
              }
              farm{
                id
                name
                numberOfLeasedHectares
                ownedHectares
                irrigatedHectares
                drylandHectares
                province
              }
              progress
              createdAt
              completed
              farmAsset{
                id
              }
              fundingAsk{
                fundingRequestCosts{
                  id
                  label
                  cost
                }
                notes
                expectedFundingYear
                expectedFundingMonth
              }
              productionPlans{
                id
                crop{
                  id
                  name
                }
              }
              applicationOwnerships{
                id
                owner{
                  id
                  name
                  surname
                  pictureUrl
                }
                ownerId
                reportingTo{
                  id
                  name
                  surname
                  pictureUrl
                }
                reportingToId
                notes
                state
                createdAt
                updatedAt
              }
            }
          }`;
      graphqlRequestHandler('getAllFDApplications', query, variables, headers)
        .then((applications: IFdApplicationType[]) => resolve(applications))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdApplication(
  id: number | string,
): Promise<IFdApplicationType | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($id: Float!){
            getFDApplicationById(id: $id){
              id
              createdAt
              applicationState
              completedSections
              incompleteSections
              completedDocTypesPercentage
              incompleteDocTypes
              completedSectionsPercentage
              applyingUser{
                id
                name
                surname
                email
                phoneNumber
                creditScore
                externalId
                idNumber
              }
              company{
                id
                name
              }
              farm{
                id
                name
                description
                directions
                farmArrangement
                numberOfLeasedHectares
                ownedHectares
                irrigatedHectares
                drylandHectares
                address
                borehole
                dam
                river
                waterRights
                fullTimeFarming
                leaseEndDate
                shortTermGoals
                longTermGoals
                averageRainfall
                landComposition
                workforceNumber
                workerTrust
                employmentBeforeAndAfter
                province
              }
              progress
              createdAt
              completed
              farmAsset{
                id
              }
              fundingAsk{
                fundingRequestCosts{
                  id
                  label
                  cost
                }
                notes
                expectedFundingYear
                expectedFundingMonth
              }
              productionPlans{
                id
                crop{
                  id
                  name
                }
                numberOfHectares
                estimatedYield
                estimatedPrice
                plantingDate
                irrigation
                plantPopulation
                notes
                areaOfDryLand
                areaUnderIrrigation
              }
              team{
                id
                name
                farmTeamRole{
                  id
                  name
                  description
                }
              }
              customers{
                id
                crop{
                  id
                  name
                }
                customer{
                  id
                  name
                }
              }
              suppliers{
                id
                supplierList{
                  id
                  name
                  products{
                    id
                    name
                  }
                }
              }
              farmRisk{
                id
                risk{
                  id
                  name
                }
                problem
                mitigation
              }
              financialPerformance{
                id
                year
                revenue
                expenses
                operatingCosts
                profit
                financialYearEnd
                financialYearEndMonth
                notes
              }
              productionCosts{
                id
                crop{
                  id
                  name
                }
                plantingDate
                year
                seed
                fertiliser
                chemical
                numberOfHectares
                notes
              }
              operatingCosts{
                id
                year
                monthlyCosts
                notes
                expense{
                  id
                  name
                }
                liability{
                  id
                  name
                }
              }
              assets{
                id
                description
                model
                make
                year
                value
                condition
                pricePerUnit
                capacity
                assetType
                farm{
                  id
                }
                assetListItem{
                  id
                  name
                  assetCategory
                }
              }
              liabilities{
                id
                lender
                creditLimitAmount
                currentBalanceLimit
                monthlyInstallmentAmount
                inArrears
                liabilityAccountType{
                  id
                  name
                }
              }
              applicationDocuments{
                id
                documentType
                filePath
                createdAt
                updatedAt
              }
              productionHistory{
                id
                crop{
                  id
                  name
                }
                year
                numberOfHectaresFarmed
                yieldPerHectare
                notes
                plantYear
                plantMonth
                harvestYear
                harvestMonth
                farm{
                  id
                }
              }
              applicationLivestock{
                id
                livestock{
                  id
                  name
                  livestockType
                  livestockProduct
                }
                quantity
                type
                productGrading
                sellOfPeriod
                price
                notes
              }
              applicationWaterRights{
                id
                waterSource
                householdConsumption
                notes
                waterRights
                waterScale
                permitNumber
                controlled
                waterQuantity
                scheme
                schedule
              }
              applicationLongTermCrops{
                id
                crop{
                  id
                  name
                }
                blockNumber
                age
                numberOfTrees
                area
                trellies
                spacing
                irrigation
                type
                notes
                projectedYear
                projectedYield
                projectedPrice
                projectedArea
                historyYear1
                historyYield1
                historyArea1
                historyYear2
                historyYield2
                historyArea2
                historyYear3
                historyYield3
                historyArea3
              }
              applicationProperties{
                id
                nameOfDeed
                areaDistrict
                farmArrangement
                term
                amount
                dryland
                irrigation
                pastures
                grazing
                intensive
                other
                notes
                total
              }
            }
          }`;
      graphqlRequestHandler('getFDApplicationById', query, variables, headers)
        .then((applications: IFdApplicationType) => resolve(applications))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetUserFdApplications(
  externalUserId: number | string,
): Promise<IFdApplicationType[] | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {externalUserId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($externalUserId: ID!){
            getFDApplicationsByExternalUser(externalUserId: $externalUserId){
              id
              createdAt
              applicationState
              fundingAsk{
              fundingRequestCosts{
                  id
                  label
                  cost
                }
                notes
                expectedFundingYear
                expectedFundingMonth
              }
            }
          }`;
      graphqlRequestHandler(
        'getFDApplicationsByExternalUser',
        query,
        variables,
        headers,
      )
        .then((applications: IFdApplicationType[]) => resolve(applications))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdGetUser(
  id: number | string,
): Promise<IFdUser | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($id: Float!){
            FDGetUserById(id: $id){
              id
              name
              surname
              email
              phoneNumber
              creditScore
              company{
                id
                name
              }
              farm{
                id
                name
                numberOfLeasedHectares
                ownedHectares
                irrigatedHectares
                drylandHectares
              }
            }
          }`;
      graphqlRequestHandler('FDGetUserById', query, variables, headers)
        .then((user: IFdUser) => resolve(user || null))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetUserTraderApplications(
  userId: number | string,
): Promise<ITraderApplication[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {userId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($userId: ID!){
            getUserTraderApplications(userId: $userId){
              id
              tradeType
              company{
                id
                name
                tradingName
                vatNumber
                taxNumber
                addressLine1
                addressLine2
                city
                province
                bank{
                  id
                  name
                }
                bankAccountType{
                  id
                  name
                }
                bankBranchCode
                bankAccountNumber
              }
              farmer{
              farm{
              beeScore
              }
              }
              forms{
                id
                name
                description
                isComplete
                sections{
                  id
                  name
                  isComplete
                  fields{
                    id
                    dependsOn{
                      fieldId
                      values
                    }
                    groupBy
                    __typename
                    ... on TradingFormText{
                      textTitle: title
                      textBody: body
                      textType: type
                      groupView
                    }
                    ... on TradingFormTextInput{
                      title
                      value
                      textInputTitle: title
                      textInputValue: value
                      textInputValidationTypes: validationTypes
                      isOptional
                    }
                    ... on TradingFormSingleChoiceOption{
                      singleChoiceOptionTitle: title
                      singleChoiceOptionValue: value
                      singleChoiceOptionType: type
                      singleChoiceOptionOptions: options{
                        title
                        value
                      }
                    }
                    ... on TradingFormMultiChoiceOption{
                      multiChoiceOptionTitle: title
                      multiChoiceOptionValues: values
                      multiChoiceOptionOptions: options{
                        title
                        value
                      }
                    }
                    ... on TradingFormCheckBox{
                      checkBoxTitle: title
                      checkBoxValue: value
                    }
                    ... on TradingFormFileUpload{
                      fileUploadTitle: title
                      fileUploadSubTitle: subTitle
                      fileUploadIsOptional: isOptional
                      fileUploadState: state
                      fileUploadValue: value
                      allowMultiple
                    }
                  }
                }
              }
            }
          }`;
      graphqlRequestHandler(
        'getUserTraderApplications',
        query,
        variables,
        headers,
      )
        .then((applications: ITraderApplication[]) => resolve(applications))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetTraderForm(
  formId: number | string,
  userId?: number | string,
): Promise<ITradingForm | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables: Record<string, unknown> = {
      formId,
      companyEntityType: true,
    };
    if (userId != null) variables.userId = userId;
    return new Promise((resolve, reject) => {
      const query = `
          query variables($formId: ID!, $companyEntityType: Boolean, $userId: ID){
            getTradingForm(formId: $formId, companyEntityType: $companyEntityType, userId: $userId){
              id
              name
              description
              isComplete
              sections{
                id
                name
                isComplete
                fields{
                  id
                  dependsOn{
                    fieldId
                    values
                  }
                  groupBy
                  __typename
                  ... on TradingFormText{
                    textTitle: title
                    textBody: body
                    textType: type
                    groupView
                  }
                  ... on TradingFormTextInput{
                    textInputTitle: title
                    textInputValue: value
                    textInputValidationTypes: validationTypes
                    isOptional
                  }
                  ... on TradingFormSingleChoiceOption{
                    singleChoiceOptionTitle: title
                    singleChoiceOptionValue: value
                    singleChoiceOptionType: type
                    singleChoiceOptionOptions: options{
                      title
                      value
                    }
                  }
                  ... on TradingFormMultiChoiceOption{
                    multiChoiceOptionTitle: title
                    multiChoiceOptionValues: values
                    multiChoiceOptionOptions: options{
                      title
                      value
                    }
                  }
                  ... on TradingFormCheckBox{
                    checkBoxTitle: title
                    checkBoxValue: value
                  }
                  ... on TradingFormFileUpload{
                    fileUploadTitle: title
                    fileUploadSubTitle: subTitle
                    fileUploadIsOptional: isOptional
                    fileUploadState: state
                    fileUploadValue: value
                    allowMultiple
                  }
                }
              }
            }
          }`;
      graphqlRequestHandler('getTradingForm', query, variables, headers)
        .then((form: ITradingForm) => resolve(form || null))
        .catch((error) => reject(error));
    });
  });
}

export async function apiSubmitTraderFormFields(input: {
  applicationId: number | string;
  fields: ISubmitTraderFormFieldInput[];
  companyEntityType?: boolean;
}): Promise<ISubmitTraderFormFieldsResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {
      applicationId: input.applicationId,
      fields: input.fields,
      companyEntityType: input.companyEntityType ?? true,
    };

    return new Promise((resolve, reject) => {
      const query = `
        mutation variables(
          $applicationId: ID!
          $fields: [TradingFormFieldInput!]!
          $companyEntityType: Boolean
        ) {
          submitTraderFormFields(
            applicationId: $applicationId
            fields: $fields
            companyEntityType: $companyEntityType
          ) {
            success
            message
          }
        }`;

      graphqlRequestHandler('submitTraderFormFields', query, variables, headers)
        .then((response: ISubmitTraderFormFieldsResponse) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiFarmerLinkedAccounts(
  farmerId: string,
): Promise<ILinkedGpayAccount[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmerId};
    const query = `
      query getFarmerInputsLinkedAccounts($farmerId: ID!) {
        getFarmerInputsLinkedAccounts(farmerId: $farmerId) {
          id
          company {
            id
            name
            image
          }
          accounts {
            id
            accountType
            accountName
            AccountBalance
            AvailableBalance
            withdrawable
            accountNumber
          }
          withdrawableAmounts {
            id
            transactionName
            amount
            availableBalance
            withdrawalRequests {
              id
              transactionName
              amount
              approved
            }
          }
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getFarmerInputsLinkedAccounts',
        query,
        variables,
        headers,
      )
        .then((response: any) => resolve(response))
        .catch((error: any) => reject(error));
    });
  });
}

export async function apiGetAccountRestrictions(id: string): Promise<any[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const query = `
      query getAccountRestrictions($id: ID!) {
        getAccountRestrictions(id: $id) {
          id
          subCategory {
            id
            name
            image
          }
          categories{
            id
            name
            image
          }
          restictionAmount
        }
      }
    `;
    const variables = {id};
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('getAccountRestrictions', query, variables, headers)
        .then((response: any) => {
          if (response?.data?.getAccountRestrictions) {
            resolve(response.data.getAccountRestrictions);
          } else {
            resolve(response);
          }
        })
        .catch((error: any) => reject(error));
    });
  });
}

export async function apiGetAccountStatement(
  gpayId: string,
  accountType: EGPAY_ACCOUNT_TYPE,
  startDate?: string,
  endDate?: string,
): Promise<IGpayAccount> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const query = `
      query getFarmerInputsWalletAccountStatement
      ($gpayId: ID, $period: StatementPeriodInput, $accountType: ACCOUNT_TYPE) {
        getFarmerInputsWalletAccountStatement(gpayId: $gpayId, period: $period, accountType: $accountType) {
          accountName
          AccountBalance
          AvailableBalance
          Statement {
            TransactionDate
            SyncDate
            Description
            Reference
            SupplierBankReference
            Amount
            InvoiceNumber
          }
        }
      }
    `;
    const variables = {
      gpayId,
      accountType,
      period: {startDate, endDate},
    };
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getFarmerInputsWalletAccountStatement',
        query,
        variables,
        headers,
      )
        .then((response: any) => {
          if (response?.data?.getFarmerInputsWalletAccountStatement) {
            resolve(response.data.getFarmerInputsWalletAccountStatement);
          } else {
            resolve(response);
          }
        })
        .catch((error: any) => reject(error));
    });
  });
}

export async function apiCreateAccountRestriction(
  accountId: string,
  categoryId: string,
  amount: number,
): Promise<{success: boolean; message: string}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const mutation = `
      mutation createAccountRestriction($accountId: ID!, $categoryId: ID!, $amount: Float!) {
        createAccountRestriction(accountId: $accountId, categoryId: $categoryId, amount: $amount) {
          success
          message
        }
      }
    `;
    const variables = {accountId, categoryId, amount};
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createAccountRestriction',
        mutation,
        variables,
        headers,
      )
        .then((response: any) => {
          if (response?.data?.createAccountRestriction) {
            resolve(response.data.createAccountRestriction);
          } else {
            resolve(response);
          }
        })
        .catch((error: any) => reject(error));
    });
  });
}

export async function apiGetFdCrops(): Promise<IFdCrop[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query {
            getAllFDCrops{
              id
              name
              active
            }
          }`;
      graphqlRequestHandler('getAllFDCrops', query, variables, headers)
        .then((crops: IFdCrop[]) => resolve(crops || []))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFarm(farmData: IUpdateFdFarm): Promise<IFdFarm> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {farmData};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variables($farmData: UpdateFarmDTO!) {
          updateFDFarm(farmData: $farmData) {
            id
            name
            address
            description
            directions
            numberOfLeasedHectares
            leaseEndDate
            ownedHectares
            farmArrangement
            fullTimeFarming
            irrigatedHectares
            drylandHectares
            borehole
            dam
            river
            waterRights
            shortTermGoals
            longTermGoals
            landComposition
            workforceNumber
            workerTrust
            employmentBeforeAndAfter
          }
        }`;
      graphqlRequestHandler('updateFDFarm', query, variables, headers)
        .then((farm: IFdFarm) => resolve(farm))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdProductionPlans(
  productionPlansData: ISaveFdProductionPlanDto[],
): Promise<IFdProductionPlan[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {productionPlansData};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variables(
          $productionPlansData: [SaveProductionPlanDTO!]!
        ) {
          createFDAddProductionPlans(productionPlansData: $productionPlansData) {
            id
            crop {
              id
              name
            }
            numberOfHectares
            estimatedYield
            plantingDate
            notes
            irrigation
            plantPopulation
            estimatedPrice
            areaOfDryLand
            areaUnderIrrigation
            harvestYear
            harvestMonth
          }
        }`;
      graphqlRequestHandler(
        'createFDAddProductionPlans',
        query,
        variables,
        headers,
      )
        .then((productionPlans: IFdProductionPlan[]) =>
          resolve(productionPlans),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdProductionPlansByApplicationId(
  applicationId: string | number,
): Promise<IFdProductionPlan[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: Number(applicationId)};
    return new Promise((resolve, reject) => {
      const query = `
        query getFDProductionPlansByApplictionId($id: Float!) {
          getFDProductionPlansByApplictionId(id: $id) {
            id
            crop {
              id
              name
            }
            numberOfHectares
            estimatedYield
            plantingDate
            notes
            irrigation
            plantPopulation
            estimatedPrice
            areaOfDryLand
            areaUnderIrrigation
            harvestYear
            harvestMonth
          }
        }`;
      graphqlRequestHandler(
        'getFDProductionPlansByApplictionId',
        query,
        variables,
        headers,
      )
        .then((productionPlans: IFdProductionPlan[]) =>
          resolve(productionPlans || []),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteFdProductionPlan(
  id: string | number,
): Promise<{id: number}> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: Number(id)};
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation deleteFDProductionPlan($id: Float!) {
          deleteFDProductionPlan(id: $id) {
            id
          }
        }`;
      graphqlRequestHandler(
        'deleteFDProductionPlan',
        mutation,
        variables,
        headers,
      )
        .then((response: {id: number}) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdProjectedRevenuesByApplicationId(
  applicationId: string | number,
): Promise<IFdProjectedRevenueLookup[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: Number(applicationId)};
    return new Promise((resolve, reject) => {
      const query = `
        query getFDProjectedRevenuesByApplicationId($id: Float!) {
          getFDProjectedRevenuesByApplicationId(id: $id) {
            id
            monthlyCosts
            year
            crop {
              id
              name
            }
          }
        }`;
      graphqlRequestHandler(
        'getFDProjectedRevenuesByApplicationId',
        query,
        variables,
        headers,
      )
        .then((records: IProjectedRevenueRecord[]) =>
          resolve(
            (records || []).map((record) => ({
              id: String(record.id),
              cropId: record.crop?.id ? String(record.crop.id) : '',
              cropName: record.crop?.name || 'Unknown crop',
              year: Number(record.year ?? 0),
              monthlyCosts: record.monthlyCosts,
              window: lshParseProjectedRevenueWindow(record.monthlyCosts) || {
                startMonth: '',
                endMonth: '',
              },
            })),
          ),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpsertFdProjectedRevenue(input: {
  id?: string;
  monthlyCosts: IFdProjectedRevenueWindow;
  year: number;
  cropId: number;
  applicationId: number;
  notes?: string;
}): Promise<IFdProjectedRevenueLookup | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {
      createProjectedRevenueData: {
        ...(input.id ? {id: input.id} : {}),
        monthlyCosts: JSON.stringify(input.monthlyCosts),
        year: input.year,
        cropId: input.cropId,
        applicationId: input.applicationId,
        notes: input.notes,
      },
    };
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation createFDProjectedRevenue(
          $createProjectedRevenueData: CreateProjectedRevenueDTO!
        ) {
          createFDProjectedRevenue(
            createProjectedRevenueData: $createProjectedRevenueData
          ) {
            id
            monthlyCosts
            year
            crop {
              id
              name
            }
          }
        }`;
      graphqlRequestHandler(
        'createFDProjectedRevenue',
        mutation,
        variables,
        headers,
      )
        .then((record: IProjectedRevenueRecord | null) => {
          if (!record) {
            resolve(null);
            return;
          }
          resolve({
            id: String(record.id),
            cropId: record.crop?.id ? String(record.crop.id) : '',
            cropName: record.crop?.name || 'Unknown crop',
            year: Number(record.year ?? 0),
            monthlyCosts: record.monthlyCosts,
            window: lshParseProjectedRevenueWindow(record.monthlyCosts) || {
              startMonth: '',
              endMonth: '',
            },
          });
        })
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdProductionHistory(
  productionHistoryData: ISaveFdProductionHistory,
): Promise<IFdProductionHistory> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {productionHistoryData};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variable($productionHistoryData: ProductionHistoryDTO!) {
          createFDProductionHistory(productionHistoryData: $productionHistoryData) {
            id
            crop {
              id
              name
            }
            numberOfHectaresFarmed
            yieldPerHectare
            year
            notes
            plantYear
            plantMonth
            harvestYear
            harvestMonth
          }
        }
      `;
      graphqlRequestHandler(
        'createFDProductionHistory',
        query,
        variables,
        headers,
      )
        .then((productionHistory: IFdProductionHistory) =>
          resolve(productionHistory),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdProductionHistoryByApplicationId(
  applicationId: number | string,
): Promise<IFdProductionHistory[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: Number(applicationId)};
    return new Promise((resolve, reject) => {
      const query = `
        query getFDProductionHistoryByApplicationId($id: Float!) {
          getFDProductionHistoryByApplicationId(id: $id) {
            id
            crop {
              id
              name
            }
            numberOfHectaresFarmed
            yieldPerHectare
            year
            notes
            plantYear
            plantMonth
            harvestYear
            harvestMonth
          }
        }`;
      graphqlRequestHandler(
        'getFDProductionHistoryByApplicationId',
        query,
        variables,
        headers,
      )
        .then((records: IFdProductionHistory[]) => resolve(records || []))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteFdProductionHistory(
  id: string | number,
): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: String(id)};
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation deleteFDProductionHistory($id: ID!) {
          deleteFDProductionHistory(id: $id)
        }`;
      graphqlRequestHandler(
        'deleteFDProductionHistory',
        mutation,
        variables,
        headers,
      )
        .then((response: boolean) => resolve(Boolean(response)))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdFinancialPerformance(
  financialPerformanceData: ISaveFdFinancialPerformance,
): Promise<IFdFinancialPerformance> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {financialPerformanceData};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variable($financialPerformanceData: CreateFinancialPerformanceDTO!) {
          upsertFDFinancialPerformance(financialPerformanceData: $financialPerformanceData) {
            id
            year
            revenue
            expenses
            operatingCosts
            profit
            financialYearEnd
            financialYearEndMonth
            notes
          }
        }
      `;
      graphqlRequestHandler(
        'upsertFDFinancialPerformance',
        query,
        variables,
        headers,
      )
        .then((financialPerformance: IFdFinancialPerformance) =>
          resolve(financialPerformance),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiCreateFdApplicationDoc(
  applicationDocData: ISaveFdDoc,
): Promise<IFdApplicationDocType> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {applicationDocData};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variables($applicationDocData: CreateApplicationDocDTO!) {
          FDCreateApplicationDoc(applicationDocData: $applicationDocData) {
            id
            documentType
            filePath
            createdAt
            updatedAt
          }
        }
      `;
      graphqlRequestHandler('FDCreateApplicationDoc', query, variables, headers)
        .then((doc: IFdApplicationDocType) => resolve(doc))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteFdApplicationDoc(
  docId: number,
): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {docId};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variables($docId: Float!) {
          FDDeleteApplicationDoc(docId: $docId)
        }
      `;
      graphqlRequestHandler('FDDeleteApplicationDoc', query, variables, headers)
        .then((deleted: boolean) => resolve(deleted))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdApplicationProgress(input: {
  applicationId: number;
  completed: boolean;
  progress: number;
  subProgress: number;
  state?: EApplicationStateType;
}): Promise<IFdApplicationType> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {...input};
    const mutation = `
      mutation updateFDApplicationProgress(
        $applicationId: Float!
        $completed: Boolean!
        $progress: Float!
        $subProgress: Float
        $state: EApplicationStateType
      ) {
        updateFDApplicationProgress(
          applicationId: $applicationId
          completed: $completed
          progress: $progress
          subProgress: $subProgress
          state: $state
        ) {
          id
          completed
          subProgress
          progress
          applicationState
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'updateFDApplicationProgress',
        mutation,
        variables,
        headers,
      )
        .then((application: IFdApplicationType) => resolve(application))
        .catch(reject);
    });
  });
}

export async function apiGetFdAgreementTerms(): Promise<IFdAgreementDocType[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const query = `
      query getFDAgreementTerms {
        getFDAgreementTerms {
          id
          agreement
          agreementType
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('getFDAgreementTerms', query, {}, headers)
        .then((terms: IFdAgreementDocType[]) => resolve(terms || []))
        .catch(reject);
    });
  });
}

export async function apiGetFdApplicationAgreementsByApplicationId(
  applicationId: number,
): Promise<IFdApplicationAgreementType[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId};
    const query = `
      query getFDApplicationAgreementsByApplicationId($applicationId: Float!) {
        getFDApplicationAgreementsByApplicationId(applicationId: $applicationId) {
          id
          signatureUrl
          createdAt
          agreementDoc {
            id
            agreement
            agreementType
          }
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getFDApplicationAgreementsByApplicationId',
        query,
        variables,
        headers,
      )
        .then((agreements: IFdApplicationAgreementType[]) =>
          resolve(agreements || []),
        )
        .catch(reject);
    });
  });
}

export async function apiCreateFdApplicationAgreement(input: {
  applicationId: number;
  agreementDocId: number;
  signature: string;
}): Promise<IFdApplicationAgreementType> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {...input};
    const mutation = `
      mutation createFDApplicationAgreement(
        $applicationId: Float!
        $agreementDocId: Float!
        $signature: String!
      ) {
        createFDApplicationAgreement(
          applicationId: $applicationId
          agreementDocId: $agreementDocId
          signature: $signature
        ) {
          id
          signatureUrl
          createdAt
          agreementDoc {
            id
            agreement
            agreementType
          }
          application {
            id
            progress
            completed
            applicationState
          }
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createFDApplicationAgreement',
        mutation,
        variables,
        headers,
      )
        .then((agreement: IFdApplicationAgreementType) => resolve(agreement))
        .catch(reject);
    });
  });
}

export async function apiGetFdOperatingExpenses(): Promise<IGenericType[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
        query{
          getAllFDExpenses {
              id
              name
            }
        }
      `;
      graphqlRequestHandler('getAllFDExpenses', query, variables, headers)
        .then((expenses: IGenericType[]) => resolve(expenses))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpsertFdOperatingCost(
  operatingCostData: ISaveFdOperatingCost,
): Promise<IFdOperatingCost> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {operatingCostData};
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation upsertFDOperatingCost($operatingCostData: CreateOperatingCostDTO!) {
          upsertFDOperatingCost(operatingCostData: $operatingCostData) {
            id
            notes
          }
        }
      `;
      graphqlRequestHandler(
        'upsertFDOperatingCost',
        mutation,
        variables,
        headers,
      )
        .then((result: IFdOperatingCost) => resolve(result))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteFdOperatingCost(id: number): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation deleteFDOperatingCost($id: Float!) {
          deleteFDOperatingCost(id: $id)
        }
      `;
      graphqlRequestHandler(
        'deleteFDOperatingCost',
        mutation,
        variables,
        headers,
      )
        .then((deleted: boolean) => resolve(deleted))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteFarmAsset(id: number): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation deleteFDFarmAsset($id: Float!) {
          deleteFDFarmAsset(id: $id)
        }
      `;
      graphqlRequestHandler('deleteFDFarmAsset', mutation, variables, headers)
        .then((deleted: boolean) => resolve(deleted))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdAssets(): Promise<IFdFarmAssetList[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
        query{
          getAllFDFarmAssetList {
              id
              name
              assetCategory
              assetUnit
              description
          }
        }
      `;
      graphqlRequestHandler('getAllFDFarmAssetList', query, variables, headers)
        .then((assets: IFdFarmAssetList[]) => resolve(assets))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpsertFarmAsset(
  assetData: ISaveFdFarmAsset,
): Promise<IFdFarmAssetList> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {assetData};
    const mutation = `
      mutation upsertFarmAsset($assetData: FarmAssetDTO!) {
        upsertFarmAsset(assetData: $assetData) {
          id
          description
          year
          condition
          value
          pricePerUnit
          model
          make
          capacity
          assetType
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('upsertFarmAsset', mutation, variables, headers)
        .then((result: IFdFarmAssetList) => resolve(result))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdLiabilities(): Promise<IGenericType[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
        query{
          getAllFDLiabilityTypes {
              id
              name
          }
        }
      `;
      graphqlRequestHandler('getAllFDLiabilityTypes', query, variables, headers)
        .then((response: IGenericType[]) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpsertFdLiability(
  liabilityData: ISaveLiability,
): Promise<IFdLiability> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {liabilityData};
    const mutation = `
      mutation upsertFDLiability($liabilityData: CreateLiabilityDTO!) {
        upsertFDLiability(liabilityData: $liabilityData) {
          id
          creditLimitAmount
          currentBalanceLimit
          monthlyInstallmentAmount
          inArrears
          lender
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('upsertFDLiability', mutation, variables, headers)
        .then((result: IFdLiability) => resolve(result))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteFdLiability(id: number): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    const mutation = `
      mutation deleteFDLiability($id: Float!) {
        deleteFDLiability(id: $id)
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('deleteFDLiability', mutation, variables, headers)
        .then((result: boolean) => resolve(result))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpsertFdProductionCost(
  productionCostData: ISaveProductionCost,
): Promise<IFdProductionCost> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {productionCostData};
    const query = `
      mutation variables($productionCostData: CreateProductionCostDTO!) {
        upsertFDProductionCost(productionCostData: $productionCostData) {
          id
          plantingDate
          year
          numberOfHectares
          seed
          fertiliser
          chemical
          notes
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('upsertFDProductionCost', query, variables, headers)
        .then((response: IFdProductionCost) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGenerateBusinessPlan(
  applicationId: number | string,
): Promise<IAiBusinessPlan> {
  const token = await lshGETAUTH();
  const variables = {applicationId: Number(applicationId)};
  const query = `
    query variables($applicationId: Float!){
      generateAiBusinessPlan(applicationId: $applicationId){
        executiveSummary
        companyOverview{
          owner
          experience
          farmName
          landDetails
          waterRights
        }
        assets{
          description{risks strengths notes}
          content{name value year condition}
        }
        liabilities{
          description{risks strengths notes}
          content{name limit balance installment}
        }
        fundingRequest{
          description{risks strengths notes}
          content{name value}
        }
        productionPlan{
          description{risks strengths notes}
          content{
            crop
            hectares
            plantingDate
            plantPopulationPerHectare
            irrigation
            estimatedYieldTPerHectare
            estimatedPricePerT
          }
        }
        productionCosts{
          description{risks strengths notes}
          content{name value}
        }
        operatingCosts{
          description{risks strengths notes}
          content{name value}
        }
        financialProjections{
          description{risks strengths notes}
          content{revenue costs netProfit}
        }
        team{
          description{risks strengths notes}
          content{name role}
        }
        customers{
          description{risks strengths notes}
          content{name crops}
        }
        suppliers{name crops}
        applicationStrengths
        applicationWeaknesses
        applicationMitigation{
          description
          content{risk mitigation}
        }
        productionHistory{
          description{risks strengths notes}
          content{year crop hectares yield}
        }
        financialHistory{
          description{risks strengths notes}
          content{year revenue costs netProfit}
        }
        conclusion{
          description
          recommendations
        }
      }
    }`;
  return graphqlRequestHandler('generateAiBusinessPlan', query, variables, {
    Authorization: token,
  });
}

export async function apiUpdateFdApplicationState(
  applicationId: string | number,
  newState: EApplicationStateType,
): Promise<IFdApplicationType> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {
      applicationId: String(applicationId),
      state: newState,
    };
    const query = `
      mutation updateFDApplicationState(
        $applicationId: ID!
        $state: EApplicationStateType!
      ) {
        updateFDApplicationState(
          applicationId: $applicationId
          state: $state
        ) {
          id
          applicationState
          completedSections
          incompleteSections
          completedSectionsPercentage
          progress
          subProgress
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'updateFDApplicationState',
        query,
        variables,
        headers,
      )
        .then((updated: IFdApplicationType) => resolve(updated))
        .catch(reject);
    });
  });
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export async function apiCreateOrUpdateFDApplicationOwnership(
  applicationOwnership: ICreateApplicationOwnershipDto,
): Promise<IApplicationOwnershipType> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const mapped = {
      id: applicationOwnership.id,
      applicationId: String(applicationOwnership.applicationId),
      ownerId: String(applicationOwnership.ownerId),
      reportingToId: String(applicationOwnership.reportingToId),
      state: applicationOwnership.state,
      notes: applicationOwnership.notes,
      handOver: applicationOwnership.handOver,
    } as any;
    const variables = {applicationOwnership: mapped};
    const query = `
      mutation variables($applicationOwnership: CreateApplicationOwnershipDTO!) {
        createOrUpdateFDApplicationOwnership(
          applicationOwnership: $applicationOwnership
        ) {
          id
          owner { id name surname }
          ownerId
          reportingTo { id name surname }
          reportingToId
          state
          notes
          createdAt
          updatedAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createOrUpdateFDApplicationOwnership',
        query,
        variables,
        headers,
      )
        .then((resp: IApplicationOwnershipType) => resolve(resp))
        .catch(reject);
    });
  });
}

export async function apiGetAllFarmTeamRoles(): Promise<IGenericType[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const query = `
      query getAllFDFarmTeamRoles {
        getAllFDFarmTeamRoles {
          id
          name
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('getAllFDFarmTeamRoles', query, {}, headers)
        .then((roles: IGenericType[]) => resolve(roles))
        .catch(reject);
    });
  });
}

export async function apiGetFarmTeamsByApplicationId(
  applicationId: string | number,
): Promise<IFarmTeam[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: Number(applicationId)};
    const query = `
      query getFDFarmTeamsByApplicationId($id: Float!) {
        getFDFarmTeamsByApplicationId(id: $id) {
          id
          name
          description
          farmTeamRole {
            id
            name
          }
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getFDFarmTeamsByApplicationId',
        query,
        variables,
        headers,
      )
        .then((team: IFarmTeam[]) => resolve(team))
        .catch(reject);
    });
  });
}

export async function apiCreateFdFarmTeam(
  farmTeamData: CreateFarmTeamDTO,
): Promise<{id: number}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmTeamData};
    const mutation = `
      mutation createFDFarmTeam($farmTeamData: CreateFarmTeamDTO!) {
        createFDFarmTeam(farmTeamData: $farmTeamData) {
          id
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('createFDFarmTeam', mutation, variables, headers)
        .then((resp: {id: number}) => resolve(resp))
        .catch(reject);
    });
  });
}

export async function apiDeleteFdFarmTeam(id: number): Promise<{id: number}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id};
    const mutation = `
      mutation deleteFDFarmTeam($id: Float!) {
        deleteFDFarmTeam(id: $id) {
          id
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('deleteFDFarmTeam', mutation, variables, headers)
        .then((resp: {id: number}) => resolve(resp))
        .catch(reject);
    });
  });
}

export async function apiGetAllFdCustomers(): Promise<IGenericType[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const query = `
      query getAllFDCustomers {
        getAllFDCustomers {
          id
          name
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('getAllFDCustomers', query, {}, headers)
        .then((list: IGenericType[]) => resolve(list))
        .catch(reject);
    });
  });
}

export async function apiGetAllFdCrops(
  longTermCrop?: string | null,
): Promise<IGenericType[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {longTermCrop: longTermCrop ?? null};
    const query = `
      query getAllFDCrops($longTermCrop: String) {
        getAllFDCrops(longTermCrop: $longTermCrop) {
          id
          name
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('getAllFDCrops', query, variables, headers)
        .then((list: IGenericType[]) => resolve(list))
        .catch(reject);
    });
  });
}

export async function apiGetCustomerSuppliersByApplicationId(
  applicationId: string | number,
): Promise<ICustomerSupplier[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: Number(applicationId)};
    const query = `
      query getFDCustomerSuppliersByApplicationId($id: Float!) {
        getFDCustomerSuppliersByApplicationId(id: $id) {
          id
          crop {
            id
            name
          }
          customer {
            id
            name
          }
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getFDCustomerSuppliersByApplicationId',
        query,
        variables,
        headers,
      )
        .then((list: ICustomerSupplier[]) => resolve(list))
        .catch(reject);
    });
  });
}

export async function apiCreateFdCustomerSupplier(
  customerSupplierData: CreateCustomerSupplierDto,
): Promise<{id: number}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {customerSupplierData};
    const mutation = `
      mutation createFDCustomerSupplier(
        $customerSupplierData: CreateCustomerSupplierDTO!
      ) {
        createFDCustomerSupplier(customerSupplierData: $customerSupplierData) {
          id
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createFDCustomerSupplier',
        mutation,
        variables,
        headers,
      )
        .then((resp: {id: number}) => resolve(resp))
        .catch(reject);
    });
  });
}

export async function apiGetAllFdSuppliers(): Promise<
  {id: number; name: string; products: {id: number; name: string}[]}[]
> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const query = `
      query getFDAllSuppliers {
        getFDAllSuppliers {
          id
          name
          products {
            id
            name
          }
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler('getFDAllSuppliers', query, {}, headers)
        .then(resolve)
        .catch(reject),
    );
  });
}

export async function apiGetFarmerSuppliersByApplicationId(
  applicationId: string | number,
): Promise<
  {
    id: number;
    supplierList: {id: number; name: string};
    supplierProductList: {id: number; name: string};
  }[]
> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: Number(applicationId)};
    const query = `
      query getFDFarmerSuppliersByApplicationId($id: Float!) {
        getFDFarmerSuppliersByApplicationId(id: $id) {
          id
          supplierList { id name }
          supplierProductList { id name }
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDFarmerSuppliersByApplicationId',
        query,
        variables,
        headers,
      )
        .then(resolve)
        .catch(reject),
    );
  });
}

export async function apiCreateFdFarmerSupplier(
  farmerSupplierData: CreateFarmerSupplierDto,
): Promise<{id: number}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmerSupplierData};
    const mutation = `
      mutation createFDFarmerSupplier($farmerSupplierData: CreateFarmerSupplierDTO!) {
        createFDFarmerSupplier(farmerSupplierData: $farmerSupplierData) { id }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'createFDFarmerSupplier',
        mutation,
        variables,
        headers,
      )
        .then(resolve)
        .catch(reject),
    );
  });
}

export async function apiGetAllFdCommodityTypes(): Promise<IFdCommodityType[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const query = `
      query getAllFDCommodityTypes {
        getAllFDCommodityTypes {
          id
          name
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler('getAllFDCommodityTypes', query, {}, headers)
        .then((rows: IFdCommodityType[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiGetFdApplicationHistoryCommoditiesByApplicationId(
  applicationId: string | number,
): Promise<IFdApplicationHistoryCommodity[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId: String(applicationId)};
    const query = `
      query getFDApplicationHistoryCommoditiesByApplicationId($applicationId: ID!) {
        getFDApplicationHistoryCommoditiesByApplicationId(applicationId: $applicationId) {
          id
          commodityType {
            id
            name
          }
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDApplicationHistoryCommoditiesByApplicationId',
        query,
        variables,
        headers,
      )
        .then((rows: IFdApplicationHistoryCommodity[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiCreateFdApplicationHistoryCommodities(
  applicationHistoryCommodities: ICreateOrUpdateFdApplicationHistoryCommodityDto[],
): Promise<IFdApplicationHistoryCommodity[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {
      applicationHistoryCommodities: (applicationHistoryCommodities || []).map(
        (item) => ({
          applicationId: String(item.applicationId),
          commodityTypeId: String(item.commodityTypeId),
        }),
      ),
    };
    const mutation = `
      mutation createFDApplicationHistoryCommodities(
        $applicationHistoryCommodities: [CreateOrUpdateApplicationHistoryCommodityDTO!]!
      ) {
        createFDApplicationHistoryCommodities(
          applicationHistoryCommodities: $applicationHistoryCommodities
        ) {
          id
          commodityType {
            id
            name
          }
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'createFDApplicationHistoryCommodities',
        mutation,
        variables,
        headers,
      )
        .then((rows: IFdApplicationHistoryCommodity[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiGetAllFdLivestock(): Promise<
  IFdLivestockCatalogueItem[]
> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const query = `
      query getAllFDLivestock {
        getAllFDLivestock {
          id
          name
          livestockType
          livestockProduct
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler('getAllFDLivestock', query, {}, headers)
        .then((rows: IFdLivestockCatalogueItem[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiGetFdApplicationLivestockByApplicationId(
  applicationId: string | number,
): Promise<IFdApplicationLivestockRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId: String(applicationId)};
    const query = `
      query getFDApplicationLivestockByApplicationId($applicationId: ID!) {
        getFDApplicationLivestockByApplicationId(applicationId: $applicationId) {
          id
          quantity
          notes
          type
          productGrading
          sellOfPeriod
          price
          livestock {
            id
            name
            livestockType
            livestockProduct
          }
          application {
            id
          }
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDApplicationLivestockByApplicationId',
        query,
        variables,
        headers,
      )
        .then((rows: IFdApplicationLivestockRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiCreateOrUpdateMultipleFdApplicationLivestock(
  applicationLivestock: IUpsertFdApplicationLivestockDto[],
): Promise<IFdApplicationLivestockRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationLivestock};
    const mutation = `
      mutation createOrUpdateMultipleFDApplicationLivestock(
        $applicationLivestock: [CreateApplicationLivestockDTO!]!
      ) {
        createOrUpdateMultipleFDApplicationLivestock(
          applicationLivestock: $applicationLivestock
        ) {
          id
          quantity
          notes
          type
          productGrading
          sellOfPeriod
          price
          livestock {
            id
            name
            livestockType
            livestockProduct
          }
          application {
            id
          }
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'createOrUpdateMultipleFDApplicationLivestock',
        mutation,
        variables,
        headers,
      )
        .then((rows: IFdApplicationLivestockRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiDeleteFdApplicationLivestock(
  id: string | number,
): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: String(id)};
    const mutation = `
      mutation deleteFDApplicationLivestock($id: ID!) {
        deleteFDApplicationLivestock(id: $id)
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'deleteFDApplicationLivestock',
        mutation,
        variables,
        headers,
      )
        .then((result: boolean) => resolve(Boolean(result)))
        .catch(reject),
    );
  });
}

export async function apiGetFdApplicationLongTermCropsByApplicationId(
  applicationId: string | number,
): Promise<IFdApplicationLongTermCropRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId: String(applicationId)};
    const query = `
      query getFDApplicationLongTermCropsByApplicationId($applicationId: ID!) {
        getFDApplicationLongTermCropsByApplicationId(applicationId: $applicationId) {
          id
          blockNumber
          age
          numberOfTrees
          area
          trellies
          spacing
          irrigation
          type
          notes
          projectedYear
          projectedYield
          projectedPrice
          projectedArea
          historyYear1
          historyYield1
          historyArea1
          historyYear2
          historyYield2
          historyArea2
          historyYear3
          historyYield3
          historyArea3
          application {
            id
          }
          crop {
            id
            name
          }
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDApplicationLongTermCropsByApplicationId',
        query,
        variables,
        headers,
      )
        .then((rows: IFdApplicationLongTermCropRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiCreateOrUpdateFdLongTermCrops(
  applicationLongTermCrops: IUpsertFdApplicationLongTermCropDto[],
): Promise<IFdApplicationLongTermCropRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationLongTermCrops};
    const mutation = `
      mutation createOrUpdateFDLongTermCrops(
        $applicationLongTermCrops: [CreateApplicationLongTermCropDTO!]!
      ) {
        createOrUpdateFDLongTermCrops(
          applicationLongTermCrops: $applicationLongTermCrops
        ) {
          id
          blockNumber
          age
          numberOfTrees
          area
          trellies
          spacing
          irrigation
          type
          notes
          projectedYear
          projectedYield
          projectedPrice
          projectedArea
          historyYear1
          historyYield1
          historyArea1
          historyYear2
          historyYield2
          historyArea2
          historyYear3
          historyYield3
          historyArea3
          application {
            id
          }
          crop {
            id
            name
          }
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'createOrUpdateFDLongTermCrops',
        mutation,
        variables,
        headers,
      )
        .then((rows: IFdApplicationLongTermCropRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiDeleteFdApplicationLongTermCrop(
  id: string | number,
  blockNumber: string,
): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: String(id), blockNumber};
    const mutation = `
      mutation deleteFDApplicationLongTermCrop($id: ID!, $blockNumber: String!) {
        deleteFDApplicationLongTermCrop(id: $id, blockNumber: $blockNumber)
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'deleteFDApplicationLongTermCrop',
        mutation,
        variables,
        headers,
      )
        .then((result: boolean) => resolve(Boolean(result)))
        .catch(reject),
    );
  });
}

export async function apiGetFdApplicationWaterRightsByApplicationId(
  applicationId: string | number,
): Promise<IFdApplicationWaterRightRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId: String(applicationId)};
    const query = `
      query getFDApplicationWaterRightsByApplicationId($applicationId: ID!) {
        getFDApplicationWaterRightsByApplicationId(applicationId: $applicationId) {
          id
          scheme
          schedule
          waterSource
          waterRights
          waterScale
          permitNumber
          controlled
          notes
          householdConsumption
          waterQuantity
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDApplicationWaterRightsByApplicationId',
        query,
        variables,
        headers,
      )
        .then((rows: IFdApplicationWaterRightRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiCreateOrUpdateMultipleFdApplicationWaterRights(
  applicationWaterRights: IUpsertFdApplicationWaterRightDto[],
): Promise<IFdApplicationWaterRightRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationWaterRights};
    const mutation = `
      mutation createOrUpdateMultipleFDApplicationWaterRights(
        $applicationWaterRights: [UpdateApplicationWaterRightsDTO!]!
      ) {
        createOrUpdateMultipleFDApplicationWaterRights(
          applicationWaterRights: $applicationWaterRights
        ) {
          id
          scheme
          schedule
          waterSource
          waterRights
          waterScale
          permitNumber
          controlled
          notes
          householdConsumption
          waterQuantity
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'createOrUpdateMultipleFDApplicationWaterRights',
        mutation,
        variables,
        headers,
      )
        .then((rows: IFdApplicationWaterRightRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiDeleteFdApplicationWaterRights(
  id: string | number,
): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: String(id)};
    const mutation = `
      mutation deleteFDApplicationWaterRights($id: ID!) {
        deleteFDApplicationWaterRights(id: $id)
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'deleteFDApplicationWaterRights',
        mutation,
        variables,
        headers,
      )
        .then((result: boolean) => resolve(Boolean(result)))
        .catch(reject),
    );
  });
}

export async function apiDeleteFdApplicationWaterRightsByApplicationId(
  applicationId: string | number,
): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId: String(applicationId)};
    const mutation = `
      mutation deleteFDApplicationWaterRightsByApplicationId($applicationId: ID!) {
        deleteFDApplicationWaterRightsByApplicationId(applicationId: $applicationId)
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'deleteFDApplicationWaterRightsByApplicationId',
        mutation,
        variables,
        headers,
      )
        .then((result: boolean) => resolve(Boolean(result)))
        .catch(reject),
    );
  });
}

export async function apiGetFdApplicationPropertiesByApplicationId(
  applicationId: string | number,
): Promise<IFdApplicationPropertyRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId: String(applicationId)};
    const query = `
      query getFDApplicationProperties($applicationId: ID!) {
        getFDApplicationProperties(applicationId: $applicationId) {
          id
          nameOfDeed
          areaDistrict
          farmArrangement
          term
          amount
          dryland
          irrigation
          pastures
          grazing
          intensive
          other
          notes
          total
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDApplicationProperties',
        query,
        variables,
        headers,
      )
        .then((rows: IFdApplicationPropertyRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiCreateOrUpdateFdApplicationProperties(
  applicationProperties: IUpsertFdApplicationPropertyDto[],
): Promise<IFdApplicationPropertyRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationProperties};
    const mutation = `
      mutation createOrUpdateFDApplicationProperties(
        $applicationProperties: [CreateApplicationPropertyDTO!]!
      ) {
        createOrUpdateFDApplicationProperties(
          applicationProperties: $applicationProperties
        ) {
          id
          nameOfDeed
          areaDistrict
          farmArrangement
          term
          amount
          dryland
          irrigation
          pastures
          grazing
          intensive
          other
          notes
          total
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'createOrUpdateFDApplicationProperties',
        mutation,
        variables,
        headers,
      )
        .then((rows: IFdApplicationPropertyRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiDeleteFdApplicationProperty(
  id: string | number,
): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: String(id)};
    const mutation = `
      mutation deleteFDApplicationProperty($id: ID!) {
        deleteFDApplicationProperty(id: $id)
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'deleteFDApplicationProperty',
        mutation,
        variables,
        headers,
      )
        .then((result: boolean) => resolve(Boolean(result)))
        .catch(reject),
    );
  });
}

export async function apiGetAllFarmRisks(): Promise<IFarmRiskBasic[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const query = `
      query {
        getAllFDFarmRisks { id name }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler('getAllFDFarmRisks', query, {}, headers)
        .then((res) => resolve(res as IFarmRiskBasic[]))
        .catch(reject),
    );
  });
}

export async function apiGetFarmRisksByApplicationId(
  applicationId: number | string,
): Promise<IFarmRiskByApp[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: Number(applicationId)};
    const query = `
      query getFDFarmRisksByApplicationId($id: Float!) {
        getFDFarmRisksByApplicationId(id: $id) {
          id
          mitigation
          problem
          risk { id name }
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDFarmRisksByApplicationId',
        query,
        variables,
        headers,
      )
        .then((res) => resolve(res as IFarmRiskByApp[]))
        .catch(reject),
    );
  });
}

export async function apiCreateFdFarmRisk(
  farmRiskData: ICreateFarmRiskDto,
): Promise<{id: number}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmRiskData};
    const mutation = `
      mutation createFDFarmRisk($farmRiskData: CreateFarmRiskDTO!) {
        createFDFarmRisk(farmRiskData: $farmRiskData) { id }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler('createFDFarmRisk', mutation, variables, headers)
        .then(resolve)
        .catch(reject),
    );
  });
}

export async function apiDeleteFdFarmRisk(
  id: number | string,
): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: Number(id)};
    const mutation = `
      mutation deleteFDFarmRisk($id: Float!) {
        deleteFDFarmRisk(id: $id)
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler('deleteFDFarmRisk', mutation, variables, headers)
        .then((result: boolean) => resolve(Boolean(result)))
        .catch(reject),
    );
  });
}

export async function apiRunItcTraderApplication(
  data: IRunItcTraderApplicationDto,
): Promise<IitcDoc> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = data;
    const mutation = `
      mutation runItcTraderApplication(
        $name: String!
        $surname: String!
        $idNumber: String!
        $externalServiceRequest: Boolean!
      ) {
        runItcTraderApplication(
          name: $name
          surname: $surname
          idNumber: $idNumber
          externalServiceRequest: $externalServiceRequest
        ) {
          personName
          personSurname
          finalDocLink
          rawDataLink
          requester { name }
          requestedOn
        }
      }
    `;
    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'runItcTraderApplication',
        mutation,
        variables,
        headers,
      )
        .then(resolve)
        .catch(reject),
    );
  });
}

export async function apiPerformCompanyCreditCheck(
  payload: IPerformCompanyCreditCheckDto,
): Promise<ICompanyItcDoc> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {...payload};
    const mutation = `
      query performCompanyCreditCheck(
        $regNumber1: String!
        $regNumber2: String!
        $regNumber3: String!
        $refresh: Boolean!
      ) {
        performCompanyCreditCheck(
          regNumber1: $regNumber1
          regNumber2: $regNumber2
          regNumber3: $regNumber3
          refresh: $refresh
        ) {
          id
          companyName
          companyRegNumber
          finalDocLink
          rawDataLink
          requester { name }
        }
      }
    `;
    return new Promise<ICompanyItcDoc>((resolve, reject) =>
      graphqlRequestHandler(
        'performCompanyCreditCheck',
        mutation,
        variables,
        headers,
      )
        .then((res) => resolve(res as ICompanyItcDoc))
        .catch(reject),
    );
  });
}

export async function apiGetFdFundingAsksByApplicationId(
  applicationId: number,
): Promise<IFdFundingAsk | undefined> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: applicationId};
    const query = `
      query getFDFundingAsksByApplicationId($id: Float!) {
        getFDFundingAsksByApplicationId(id: $id) {
          id
          fundingRequestCosts { id label cost }
          notes
          expectedFundingYear
          expectedFundingMonth
        }
      }
    `;
    return new Promise<IFdFundingAsk | undefined>((resolve, reject) =>
      graphqlRequestHandler(
        'getFDFundingAsksByApplicationId',
        query,
        variables,
        headers,
      )
        .then((res) => resolve(res as IFdFundingAsk))
        .catch(reject),
    );
  });
}

export async function apiCreateFdFundingAsk(payload: {
  applicationId: number;
  notes?: string;
  expectedFundingYear?: number;
  expectedFundingMonth?: number;
  fundingRequestCosts: IFdFundingRequestCost[];
}): Promise<IFdFundingAsk> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {data: payload};
    const mutation = `
      mutation createFDFundingAsk($data: CreateFundingAskDTO!) {
        createFDFundingAsk(data: $data) {
          id
          fundingRequestCosts { id label cost }
          notes
          expectedFundingYear
          expectedFundingMonth
        }
      }
    `;
    return new Promise<IFdFundingAsk>((resolve, reject) =>
      graphqlRequestHandler('createFDFundingAsk', mutation, variables, headers)
        .then((res) => resolve(res as IFdFundingAsk))
        .catch(reject),
    );
  });
}

export async function apiDeleteFarmerSupplier(payload: {
  applicationId: number;
  supplierId: number;
  supplierProductId: number;
}): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {...payload};
    const mutation = `
      mutation deleteFarmerSupplier(
        $applicationId: Float!
        $supplierId: Float!
        $supplierProductId: Float!
      ) {
        deleteFarmerSupplier(
          applicationId: $applicationId
          supplierId: $supplierId
          supplierProductId: $supplierProductId
        )
      }
    `;
    return new Promise<boolean>((resolve, reject) =>
      graphqlRequestHandler(
        'deleteFarmerSupplier',
        mutation,
        variables,
        headers,
      )
        .then((res) => resolve(res as boolean))
        .catch(reject),
    );
  });
}

export async function apiDeleteCustomerSupplier(payload: {
  applicationId: number;
  customerId: number;
  cropId: number;
}): Promise<boolean> {
  const headers = {Authorization: await lshGETAUTH()};
  const variables = {
    applicationId: Number(payload.applicationId),
    customerId: Number(payload.customerId),
    cropId: Number(payload.cropId),
  };
  const mutation = `
    mutation deleteCustomerSupplier(
      $applicationId: Float!
      $customerId: Float!
      $cropId: Float!
    ) {
      deleteCustomerSupplier(
        applicationId: $applicationId
        customerId: $customerId
        cropId: $cropId
      )
    }
  `;
  return new Promise<boolean>((resolve, reject) =>
    graphqlRequestHandler(
      'deleteCustomerSupplier',
      mutation,
      variables,
      headers,
    )
      .then((res) => resolve(Boolean(res)))
      .catch(reject),
  );
}

export async function apiGetFdUserByEmail(
  email: string,
): Promise<IFdUser | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {email};
    return new Promise((resolve, reject) => {
      const query = `
        query variables($email: String!){
          FDGetUserByEmail(email: $email){
            id
            name
            surname
            email
            phoneNumber
            company{
              id
              name
              address
            }
            farm{
              id
              name
              province
              address
              ownedHectares
            }
            financials{
              vatNumber
              taxNumber
            }
          }
        }`;
      graphqlRequestHandler('FDGetUserByEmail', query, variables, headers)
        .then((user: IFdUser) => resolve(user || null))
        .catch((error) => reject(error));
    });
  });
}

export async function apiInitiateFdApplication(input: {
  applyingUser: string | number;
  farm: string | number;
  company: string | number;
}): Promise<{id: string}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {
      applyingUser: Number(input.applyingUser),
      farm: Number(input.farm),
      company: Number(input.company),
    };
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($applyingUser: ID!, $farm: Float!, $company: Float!){
            createFDSupplierApplication(applicationData: {
              applyingUser: $applyingUser,
              farm: $farm,
              company: $company
            }){
              id
            }
          }`;
      graphqlRequestHandler(
        'createFDSupplierApplication',
        query,
        variables,
        headers,
      )
        .then((result: any) =>
          resolve(result?.createFDSupplierApplication ?? {id: ''}),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdUser(userData: {
  id: number;
  name?: string;
  surname?: string;
  email?: string;
  phoneNumber?: string;
}): Promise<IFdUser> {
  return lshGETAUTH().then((data) => {
    const headers: Record<string, string> = {Authorization: data};
    const variables = {userData};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($userData: UpdateUserDto!){
            updateFDUser(userData: $userData){
              id
              name
              surname
              email
              phoneNumber
            }
          }`;
      graphqlRequestHandler('updateFDUser', query, variables, headers)
        .then((user: IFdUser) => resolve(user))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdCompany(companyDetails: {
  id: number;
  name?: string;
  address?: string;
  email?: string;
  phoneNumber?: string;
}): Promise<IFdCompany> {
  return lshGETAUTH().then((data) => {
    const headers: Record<string, string> = {Authorization: data};
    const variables = {companyDetails};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($companyDetails: UpdateCompanyDTO!){
            updateFDCompany(companyDetails: $companyDetails){
              id
              name
              address
            }
          }`;
      graphqlRequestHandler('updateFDCompany', query, variables, headers)
        .then((company: IFdCompany) => resolve(company))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdFarm(farmData: {
  farmId?: number;
  applyingUserId?: number;
  name?: string;
  province?: string;
  address?: string;
  ownedHectares?: number;
}): Promise<IFdFarm> {
  return lshGETAUTH().then((data) => {
    const headers: Record<string, string> = {Authorization: data};
    const variables = {farmData};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($farmData: UpdateFarmDTO!){
            updateFDFarm(farmData: $farmData){
              id
              name
              province
              address
              ownedHectares
            }
          }`;
      graphqlRequestHandler('updateFDFarm', query, variables, headers)
        .then((farm: IFdFarm) => resolve(farm))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdFarmFinancials(farmFinancialsData: {
  companyId: number;
  vatNumber?: string;
  taxNumber?: string;
  bankName?: string;
  accountNumber?: string;
}): Promise<{
  id: number;
  vatNumber: string;
  taxNumber: string;
  bankName: string;
  accountNumber: string;
}> {
  return lshGETAUTH().then((data) => {
    const headers: Record<string, string> = {Authorization: data};
    const variables = {farmFinancialsData};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($farmFinancialsData: UpdateFarmFinancialsDTO!){
            updateFDFarmFinancials(farmFinancialsData: $farmFinancialsData){
              id
              vatNumber
              taxNumber
              bankName
              accountNumber
            }
          }`;
      graphqlRequestHandler('updateFDFarmFinancials', query, variables, headers)
        .then((result: any) => resolve(result))
        .catch((error) => reject(error));
    });
  });
}
